import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BulkCompleteRequestsAPI } from "api/requests";
import { GetRequestDetailsAPI } from "api/requests";
import { GetRequestsByStayAPI } from "api/requests";
import { SoftDeleteRequestAPI } from "api/requests";
import { UpdateRequestAPI } from "api/requests";
import { GetRequestsAPI } from "api/requests";
import { CreateRequestAPI } from "api/requests";

export const createRequest = createAsyncThunk(
  "requests/createRequest",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await CreateRequestAPI(payload);

      return response?.data?.data?.request;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to create request"
      );
    }
  }
);

export const getRequests = createAsyncThunk(
  "requests/getRequests",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetRequestsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch requests"
      );
    }
  }
);

export const getRequestsByStay = createAsyncThunk(
  "requests/getRequestsByStay",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetRequestsByStayAPI(payload);
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch stay requests"
      );
    }
  }
);

export const getRequestDetails = createAsyncThunk(
  "requests/getRequestDetails",
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await GetRequestDetailsAPI(requestId);
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch request details"
      );
    }
  }
);

export const updateRequest = createAsyncThunk(
  "requests/updateRequest",
  async ({ requestId, payload }, { rejectWithValue }) => {
    try {
      const response = await UpdateRequestAPI({
        requestId,
        data: payload,
      });

      return response.data.data.request;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to update request");
    }
  }
);

export const bulkCompleteRequests = createAsyncThunk(
  "requests/bulkCompleteRequests",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await BulkCompleteRequestsAPI(payload);

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to complete requests");
    }
  }
);

export const softDeleteRequest = createAsyncThunk(
  "requests/softDeleteRequest",
  async (requestId, { rejectWithValue }) => {
    try {
      const res = await SoftDeleteRequestAPI(requestId);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to delete request");
    }
  }
);

const initialState = {
  requests: [],
  requestDetails: null,
  requestLogs: [],
  stayRequests: [],

  page: 0,
  pageSize: 10,
  total: 0,

  listLoading: false,
  listError: null,
  listSuccess: false,

  stayRequestsLoading: false,
  stayRequestsError: null,
  stayRequestsSuccess: false,

  detailsLoading: false,
  detailsError: null,
  detailsSuccess: false,

  createRequestLoading: false,
  createRequestError: null,
  createRequestSuccess: false,

  updateRequestLoading: false,
  updateRequestError: null,
  updateRequestSuccess: false,

  bulkCompleteLoading: false,
  bulkCompleteError: null,
  bulkCompleteSuccess: false,

  deleteLoading: false,
  deleteSuccess: false,
  deleteError: null,
};

const requestsSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    resetCreateRequestState: (state) => {
      state.createRequestLoading = false;
      state.createRequestError = null;
      state.createRequestSuccess = false;
    },

    clearRequests: (state) => {
      state.requests = [];
    },

    resetUpdateRequestState: (state) => {
      state.updateRequestLoading = false;
      state.updateRequestError = null;
      state.updateRequestSuccess = false;
    },

    resetBulkCompleteState: (state) => {
      state.bulkCompleteLoading = false;
      state.bulkCompleteError = null;
      state.bulkCompleteSuccess = false;
    },
    resetRequestDetailsState: (state) => {
      state.requestDetails = null;
      state.requestLogs = [];
      state.detailsLoading = false;
      state.detailsError = null;
      state.detailsSuccess = false;
    },

    resetStayRequestsState: (state) => {
      state.stayRequests = [];
      state.stayRequestsLoading = false;
      state.stayRequestsError = null;
      state.stayRequestsSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ================= CREATE REQUEST =================
      .addCase(createRequest.pending, (state) => {
        state.createRequestLoading = true;
        state.createRequestError = null;
        state.createRequestSuccess = false;
      })

      .addCase(createRequest.fulfilled, (state, action) => {
        state.createRequestLoading = false;
        state.createRequestSuccess = true;

        if (action.payload) {
          state.requests.unshift(action.payload);
        }
      })

      .addCase(createRequest.rejected, (state, action) => {
        state.createRequestLoading = false;
        state.createRequestError = action.payload;
      })

      // ================= GET REQUESTS =================
      .addCase(getRequests.pending, (state, action) => {
        const page = action.meta?.arg?.page || 1;

        state.listError = null;

        if (page === 1) {
          state.listLoading = true;
        }
      })
      .addCase(getRequests.fulfilled, (state, action) => {
        state.listLoading = false;
        state.listSuccess = true;

        const page = action.meta?.arg?.page || 1;
        const items = action.payload?.items || [];

        if (page === 1) {
          state.requests = items;
        } else {
          state.requests = [...state.requests, ...items];
        }

        state.page = action.payload?.page || 0;
        state.pageSize = action.payload?.pageSize || 10;
        state.total = action.payload?.total || 0;
      })

      .addCase(getRequests.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload;
      })

      /* ================= GET STAY REQUESTS ================= */

      .addCase(getRequestsByStay.pending, (state) => {
        state.stayRequestsLoading = true;
        state.stayRequestsError = null;
        state.stayRequestsSuccess = false;
      })

      .addCase(getRequestsByStay.fulfilled, (state, action) => {
        state.stayRequestsLoading = false;
        state.stayRequestsSuccess = true;
        state.stayRequests = action.payload?.items || [];
      })

      .addCase(getRequestsByStay.rejected, (state, action) => {
        state.stayRequestsLoading = false;
        state.stayRequestsError = action.payload;
      })

      /* ================= GET REQUEST DETAILS ================= */

      .addCase(getRequestDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
        state.detailsSuccess = false;
      })

      .addCase(getRequestDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.detailsSuccess = true;

        state.requestDetails = action.payload?.request || null;
        state.requestLogs = action.payload?.logs || [];
      })

      .addCase(getRequestDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      })

      /* ================= UPDATE REQUEST ================= */
      .addCase(updateRequest.pending, (state) => {
        state.updateRequestLoading = true;
        state.updateRequestError = null;
        state.updateRequestSuccess = false;
      })

      .addCase(updateRequest.fulfilled, (state, action) => {
        state.updateRequestLoading = false;
        state.updateRequestSuccess = true;

        const updated = action.payload;

        const index = state.requests.findIndex((r) => r._id === updated._id);

        if (index !== -1) {
          state.requests[index] = {
            ...state.requests[index],
            ...updated,
          };
        }
      })

      .addCase(updateRequest.rejected, (state, action) => {
        state.updateRequestLoading = false;
        state.updateRequestError = action.payload;
      })

      /* ================= BULK COMPLETE REQUESTS ================= */
      .addCase(bulkCompleteRequests.pending, (state) => {
        state.bulkCompleteLoading = true;
        state.bulkCompleteError = null;
        state.bulkCompleteSuccess = false;
      })

      .addCase(bulkCompleteRequests.fulfilled, (state, action) => {
        state.bulkCompleteLoading = false;
        state.bulkCompleteSuccess = true;
        state.bulkCompleteError = null;
      })

      .addCase(bulkCompleteRequests.rejected, (state, action) => {
        state.bulkCompleteLoading = false;
        state.bulkCompleteError = action.payload;
      })

      /* =================  DELETE REQUEST ================= */
      .addCase(softDeleteRequest.pending, (state) => {
        state.deleteLoading = true;
        state.deleteSuccess = false;
        state.deleteError = null;
      })
      .addCase(softDeleteRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;

        const deletedId = action.payload?.data?.requestId;

        state.requests = state.requests.filter((r) => r._id !== deletedId);
      })
      .addCase(softDeleteRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      });
  },
});

export const {
  resetCreateRequestState,
  clearRequests,
  resetUpdateRequestState,
  resetBulkCompleteState,
  resetRequestDetailsState,
  resetStayRequestsState,
} = requestsSlice.actions;

export default requestsSlice.reducer;
